// FROZEN mock scaffolding — do NOT add new surfaces or consumers.
// Only the three legacy surfaces still on mock are exposed here: workspace
// security policy, workspace domain rules, and app roles/custom-permissions.
// Every other surface has moved to the generated client in `src/api/main`.
// New imports of this module are blocked by ESLint (no-restricted-imports);
// see ./README.md for the per-surface migration map and owning epics.
import type { DomainRule } from 'src/api/facade/types'

import {
  createAppCustomPermission,
  createAppRole,
  deleteAppCustomPermission,
  deleteAppRole,
  ensureAppMockBundle,
  listAppCustomPermissions,
  listAppRoles,
  updateAppRole,
  updateWorkspaceApp
} from './repositories/appRepo'
import {
  createWorkspaceDomainRule,
  deleteWorkspaceDomainRule,
  getWorkspaceSecurityPolicy,
  listWorkspaceDomainRules,
  updateWorkspaceDomainRule,
  updateWorkspaceSecurityPolicy
} from './repositories/securityRepo'
import { withLatency } from './utils/latency'

const DEFAULT_LATENCY_MS = 220

export const mockQueryKeys = {
  workspaceSecurity: (workspaceId: number) =>
    ['mock', 'workspace', workspaceId, 'security'] as const,
  workspaceDomains: (workspaceId: number) =>
    ['mock', 'workspace', workspaceId, 'domains'] as const,
  workspaceAudit: (workspaceId: number) =>
    ['mock', 'workspace', workspaceId, 'audit'] as const,
  workspaceApps: (workspaceId: number) =>
    ['mock', 'workspace', workspaceId, 'apps'] as const,
  appRoles: (appId: string) => ['mock', 'app', appId, 'roles'] as const,
  appCustomPermissions: (appId: string) =>
    ['mock', 'app', appId, 'custom-permissions'] as const,
  workspaceAppRecord: (appId: string) => ['mock', 'workspace-app', appId] as const
}

export const mockWorkspaceApi = {
  getSecurityPolicy: (workspaceId: number) =>
    withLatency(getWorkspaceSecurityPolicy(workspaceId), DEFAULT_LATENCY_MS),
  updateSecurityPolicy: (
    workspaceId: number,
    patch: Parameters<typeof updateWorkspaceSecurityPolicy>[1]
  ) =>
    withLatency(
      updateWorkspaceSecurityPolicy(workspaceId, patch),
      DEFAULT_LATENCY_MS
    ),
  listDomainRules: (workspaceId: number) =>
    withLatency(listWorkspaceDomainRules(workspaceId), DEFAULT_LATENCY_MS),
  createDomainRule: (
    workspaceId: number,
    payload: Omit<DomainRule, 'id' | 'workspaceId' | 'verified'>
  ) =>
    withLatency(createWorkspaceDomainRule(workspaceId, payload), DEFAULT_LATENCY_MS),
  deleteDomainRule: (workspaceId: number, ruleId: string) =>
    withLatency(deleteWorkspaceDomainRule(workspaceId, ruleId), DEFAULT_LATENCY_MS),
  verifyDomainRule: (workspaceId: number, ruleId: string) =>
    withLatency(
      updateWorkspaceDomainRule(workspaceId, ruleId, { verified: true }),
      DEFAULT_LATENCY_MS
    )
}

export const mockAppApi = {
  ensureAppMockBundle: (
    appId: string,
    workspaceId: number,
    appName: string
  ) =>
    withLatency(
      ensureAppMockBundle(appId, workspaceId, appName),
      DEFAULT_LATENCY_MS
    ),
  updateWorkspaceApp: (
    appId: string,
    patch: Parameters<typeof updateWorkspaceApp>[1]
  ) => withLatency(updateWorkspaceApp(appId, patch), DEFAULT_LATENCY_MS),
  listAppRoles: (appId: string) =>
    withLatency(listAppRoles(appId), DEFAULT_LATENCY_MS),
  listAppCustomPermissions: (appId: string) =>
    withLatency(listAppCustomPermissions(appId), DEFAULT_LATENCY_MS),
  createAppCustomPermission: (
    payload: Parameters<typeof createAppCustomPermission>[0]
  ) => withLatency(createAppCustomPermission(payload), DEFAULT_LATENCY_MS),
  deleteAppCustomPermission: (appId: string, permissionId: string) =>
    withLatency(deleteAppCustomPermission(appId, permissionId), DEFAULT_LATENCY_MS),
  createAppRole: (payload: Parameters<typeof createAppRole>[0]) =>
    withLatency(createAppRole(payload), DEFAULT_LATENCY_MS),
  updateAppRole: (
    appId: string,
    roleId: string,
    patch: Parameters<typeof updateAppRole>[2]
  ) => withLatency(updateAppRole(appId, roleId, patch), DEFAULT_LATENCY_MS),
  deleteAppRole: (appId: string, roleId: string) =>
    withLatency(deleteAppRole(appId, roleId), DEFAULT_LATENCY_MS)
}
